<script setup lang="ts">
import dayjs from 'dayjs';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onServerPrefetch, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'vuex';

import InformationBanner from '@/components/base/layout/InformationBanner.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import AccountInfoCard from '@/components/checkout/orderReceipt/AccountInfoCard.vue';
import Card from '@/components/checkout/orderReceipt/Card.vue';
import FulfillmentInfo from '@/components/checkout/orderReceipt/FulfillmentInfo.vue';
import HelpCard from '@/components/checkout/orderReceipt/HelpCard.vue';
import OrderReceiptSurvey from '@/components/checkout/orderReceipt/OrderReceiptSurvey.vue';
import OrderSummaryCard from '@/components/checkout/orderReceipt/OrderSummaryCard.vue';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { usePayment } from '@/composables/usePayment';
import { useReferralElement } from '@/composables/useReferralElement';
import { pluralizeBy } from '@/filters/pluralize';
import { filterByAutoDeliveryStatus } from '@/lib/cart/lineItem';
import { B2B_INCENTIVE, DTC_INCENTIVE } from '@/lib/referral';
import { useCustomer } from '@/stores/customer';
import { useNotifications } from '@/stores/notifications';
import { getFormattedDisplayDate } from '@/utils/autoDelivery';
import { calculateDaysBetweenDates, DateString } from '@/utils/dateTime';
import { NutsRedirect } from '@/utils/exceptions';
import { reportError } from '@/utils/reportError';

const props = defineProps<{ token: string }>();

const store = useStore();

const { cxMode, isDigitalGiftRedemption, loadOrder, order } = useCheckout(store);
const { customerEmail } = useCart(store, order);
const { customer, hasBusinessAccount } = storeToRefs(useCustomer());
const { standardShipments, specialDeliveryShipments } = useDelivery(store, order);
const { paymentItems, payments, totalSavings } = usePayment(store, cxMode, order);
const { notificationsByType } = storeToRefs(useNotifications());

const referralLinkRef = ref<HTMLElement>();

const referralElement = computed(() =>
  customer.value && !cxMode.value ? referralLinkRef.value : undefined,
);
const showPricing = computed(() => !isDigitalGiftRedemption.value);

const autoDeliveryItems = computed(() =>
  standardShipments.value.flatMap((shipment) =>
    filterByAutoDeliveryStatus(shipment.lineItems, true),
  ),
);
const startedWithCustomer = !!customer.value;

const accountStatusNotification = computed(() => {
  const messagesCombined = notificationsByType.value.errors
    .concat(notificationsByType.value.warnings)
    .concat(notificationsByType.value.notices);

  return messagesCombined.filter((message) =>
    [
      'customerCreated',
      'customerExisted',
      'customerCreationFailed',
      'customerExistedWithDifferentPassword',
    ].includes(message.key ?? ''),
  )[0];
});
const arrivalDate = computed(() => {
  let latestArrivalDate: DateString | undefined;
  standardShipments.value.forEach((shipment) => {
    if (!latestArrivalDate) {
      latestArrivalDate = shipment.customFields?.shipmentPickupLatestArrivalOn;
      return;
    }
    if (
      dayjs(shipment.customFields?.shipmentPickupLatestArrivalOn).isAfter(dayjs(latestArrivalDate))
    ) {
      latestArrivalDate = shipment.customFields?.shipmentPickupLatestArrivalOn;
    }
  });

  return `${getFormattedDisplayDate(latestArrivalDate)}`;
});

const displayAccountCard = computed(
  () => (!startedWithCustomer || !!accountStatusNotification.value) && !cxMode.value,
);

const shipDay = computed(() => {
  let earliestShipDate: DateString | undefined;
  let shipDayInfo;
  standardShipments.value.forEach((shipment) => {
    if (!shipment.customFields) return;

    if (!earliestShipDate) {
      earliestShipDate = shipment.customFields?.shipmentPickupShipOn;
      return;
    }
    if (dayjs(shipment.customFields?.shipmentPickupShipOn).isBefore(dayjs(earliestShipDate))) {
      earliestShipDate = shipment.customFields?.shipmentPickupShipOn;
    }
  });

  if (!earliestShipDate) return undefined;

  const todaysDate = dayjs().format('YYYY-MM-DD') as DateString;
  const daysToShip = calculateDaysBetweenDates(todaysDate, earliestShipDate as DateString);

  switch (daysToShip) {
    case 0:
      shipDayInfo = 'today';
      break;
    case 1:
      shipDayInfo = 'tomorrow';
      break;
    default:
      shipDayInfo = dayjs(earliestShipDate).format('dddd');
      break;
  }

  return shipDayInfo;
});

const autoDeliveryIntervalWeeks = computed(() => {
  const interval = autoDeliveryItems.value[0]?.custom?.fields.autoDeliveryInterval;
  return interval ? interval / 7 : undefined;
});

onServerPrefetch(async () => {
  try {
    await loadOrder(props.token);
  } catch (error) {
    reportError(error);
    throw new NutsRedirect('/cart');
  }
});

onMounted(() => {
  loadOrder(props.token);
});

const { showReferralElement } = useReferralElement(
  referralElement,
  'confirmation',
  hasBusinessAccount.value ? 'refer-a-business' : 'refer-a-friend',
);

onBeforeRouteLeave((to, _, next) => {
  let destination: string | undefined;
  if (to.name === 'Checkout') destination = '/cart';
  useCart(store).loadCart(true);
  next(destination);
});
</script>

<template>
  <div class="relative z-0 bg-neutral-100" data-test="checkout-receipt-page">
    <div class="max-w-5xl mx-4 pd-10 md:pd-5 md:mx-auto md:pb-36">
      <div class="pt-10 text-2xl font-semibold text-center md:text-3xl font-sofia-pro md:pt-32">
        Happy dance time! Your order has been placed!
      </div>
      <div v-if="order" class="grid grid-cols-12 gap-6 pt-10 md:pt-14">
        <div
          class="flex flex-col order-1 col-span-12 gap-6 md:col-span-7"
          data-test="checkout-receipt-page-col-main"
        >
          <Card class="text-base">
            <LargeBodyText v-if="order.orderNumber" class="font-sofia-pro">
              Order Number: {{ order.orderNumber }}
            </LargeBodyText>
            <BaseBodyText v-if="order.purchaseOrderNumber" class="mt-2">
              Purchase Order Number: {{ order.purchaseOrderNumber }}
            </BaseBodyText>
            <div class="mt-4 text-sm md:text-base">
              <div v-if="customerEmail">
                You’ll receive an order confirmation at:
                <span class="font-bold">{{ customerEmail }}</span>
              </div>
              <div v-if="shipDay" class="mt-2">
                Your order will ship {{ shipDay }} and should arrive on
                <span class="font-bold">{{ arrivalDate }}</span>
              </div>
            </div>
            <InformationBanner v-if="autoDeliveryItems.length" class="my-3 lg:my-5" variant="info">
              <BaseBodyText class="font-bold"> Auto-Delivery Subscription Created </BaseBodyText>
              <BaseBodyText v-if="autoDeliveryIntervalWeeks" class="mt-1">
                {{ autoDeliveryItems.length }} {{ pluralizeBy('product', autoDeliveryItems) }} in
                this order will ship out again in {{ autoDeliveryIntervalWeeks }} weeks.
                <a class="inline-block pl-1" href="/account/auto-delivery" @click.stop>
                  <BaseBodyText underline>Edit</BaseBodyText>
                </a>
              </BaseBodyText>
            </InformationBanner>
            <FulfillmentInfo
              class="hidden mt-6 md:block md:visible"
              :orderNumber="order.orderNumber"
              :shipments="standardShipments"
              :showPricing
            />
          </Card>
          <OrderReceiptSurvey class="visible md:hidden" />
          <OrderSummaryCard
            class="hidden md:visible md:block"
            :shipments="standardShipments"
            :showPricing
            :specialDeliveryShipments
            :paymentItems
            :payments
            :totalSavings
          />
        </div>
        <div
          class="flex flex-col order-2 col-span-12 gap-6 md:col-span-5"
          data-test="checkout-receipt-page-col-side"
        >
          <div v-if="showReferralElement">
            <a v-once href="/refer" class="text-black-link" ref="referralLinkRef">
              <Card class="flex flex-col gap-y-2 emphasized-card">
                <div class="inline-flex items-center gap-x-2">
                  <LargeBodyText class="font-sofia-pro">
                    Refer and Earn {{ hasBusinessAccount ? B2B_INCENTIVE : DTC_INCENTIVE }}
                  </LargeBodyText>
                  <SmallBodyText
                    class="font-semibold bg-nuts-amber-400 rounded px-1.5 no-underline"
                  >
                    NEW
                  </SmallBodyText>
                </div>
                <SmallBodyText class="text-black">
                  Like what you ordered? Refer us to
                  {{
                    hasBusinessAccount
                      ? `a business and get ${B2B_INCENTIVE}`
                      : `your friend and get ${DTC_INCENTIVE}`
                  }}.
                </SmallBodyText>
                <SmallBodyText class="font-semibold" underline>Refer Now</SmallBodyText>
              </Card>
            </a>
          </div>

          <AccountInfoCard v-if="displayAccountCard" :accountStatusNotification :order />
          <OrderSummaryCard
            class="md:hidden"
            :shipments="standardShipments"
            :showPricing
            :specialDeliveryShipments
            :paymentItems
            :payments
            :totalSavings
          />
          <Card class="md:hidden">
            <FulfillmentInfo
              :shipments="standardShipments"
              :showPricing
              :orderNumber="order.orderNumber"
            />
          </Card>
          <HelpCard />
          <OrderReceiptSurvey class="visible hidden md:flex" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.text-black-link {
  @apply text-black no-underline;
}

.emphasized-card {
  @apply bg-nuts-orange-100 hover:brightness-95 transition-all;
}
</style>
